import { useParams, Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import { COMICS, CATEGORIES } from '@/mocks/comics';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const comic = COMICS.find((c) => c.id === id);

  if (!comic) {
    return (
      <main className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="ri-emotion-sad-line text-5xl text-foreground-300" />
          <p className="text-foreground-500 mt-4">未找到该漫画</p>
          <Link to="/" className="text-primary-500 text-sm mt-2 inline-block hover:underline">
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const categoryLabels = comic.categories
    .filter((c) => c !== 'all')
    .map((slug) => CATEGORIES.find((cat) => cat.slug === slug))
    .filter(Boolean);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.koreancomic.cn/' },
          { '@type': 'ListItem', position: 2, name: '分类浏览', item: 'https://www.koreancomic.cn/category/all' },
          { '@type': 'ListItem', position: 3, name: comic.title, item: `https://www.koreancomic.cn/article/${comic.id}` },
        ],
      },
      {
        '@type': 'Article',
        headline: `${comic.title} - 韩国漫画网`,
        author: { '@type': 'Person', name: comic.author },
        datePublished: comic.createdAt,
        dateModified: comic.updatedAt,
        image: comic.coverImage,
        description: comic.description,
        articleBody: comic.longDescription,
        publisher: { '@type': 'Organization', name: '韩国漫画网' },
      },
    ],
  };

  return (
    <>
      <SEO
        title={`${comic.title} - ${comic.author} | 韩国漫画网`}
        description={`${comic.description} 韩国漫画网提供${comic.title}免费无删减韩漫在线阅读，${comic.status}，共${comic.chapters.length}话。`}
        keywords={`${comic.title},${comic.author},韩漫在线阅读,免费韩国漫画,${comic.tags.join(',')}`}
        ogImage={comic.coverImage}
        ogType="article"
        schema={articleSchema}
      />

      <main className="pt-20 pb-14">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/category/all" className="hover:text-primary-500 transition-colors whitespace-nowrap">分类浏览</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-700 truncate">{comic.title}</span>
          </nav>

          <div className="flex flex-col md:flex-row gap-6 md:gap-8">
            <div className="flex-shrink-0 w-full md:w-64">
              <div className="rounded-lg overflow-hidden aspect-[3/4]">
                <img
                  src={comic.coverImage}
                  alt={`${comic.title} 韩漫封面`}
                  title={`${comic.title} - 韩国漫画网 韩漫在线阅读`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">{comic.title}</h1>
              <p className="text-sm text-foreground-500 mt-2">
                <strong className="text-foreground-700">{comic.author}</strong> 著
              </p>

              <div className="flex flex-wrap items-center gap-3 mt-4">
                <span className={`text-xs px-3 py-1 rounded-full font-medium ${
                  comic.status === '连载中'
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-secondary-100 text-secondary-700'
                }`}>
                  {comic.status}
                </span>
                <div className="flex items-center gap-1 text-sm">
                  <i className="ri-star-fill text-yellow-500" />
                  <span className="font-semibold text-foreground-800">{comic.rating}</span>
                </div>
                <span className="text-sm text-foreground-500">
                  <i className="ri-eye-line mr-1" />
                  {(comic.views / 10000).toFixed(1)}万次阅读
                </span>
                <span className="text-sm text-foreground-500">
                  <i className="ri-heart-line mr-1" />
                  {(comic.favorites / 10000).toFixed(1)}万收藏
                </span>
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                {categoryLabels.map((cat) => (
                  cat && (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className="text-xs px-3 py-1 bg-background-100 text-foreground-600 rounded-full hover:bg-primary-100 hover:text-primary-600 transition-colors"
                    >
                      {cat.name}
                    </Link>
                  )
                ))}
              </div>

              <div className="mt-6">
                <h2 className="text-base font-semibold text-foreground-950 mb-2">作品简介</h2>
                <div className="text-sm text-foreground-600 leading-relaxed whitespace-pre-line">
                  {comic.longDescription}
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                {comic.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-2 py-1 bg-background-100 text-foreground-500 rounded"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-10">
            <h2 className="text-lg font-semibold text-foreground-950 mb-4">
              章节列表（共{comic.chapters.length}话）
            </h2>
            <div className="space-y-2">
              {comic.chapters.map((chapter) => (
                <div
                  key={chapter.id}
                  className="flex items-center justify-between p-3 bg-background-50 border border-background-200 rounded-lg hover:border-primary-200 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-foreground-800">
                      {chapter.title}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-foreground-400">{chapter.updatedAt}</span>
                    <button className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-play-line" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-10 flex items-center justify-between">
            <Link
              to="/category/all"
              className="text-sm text-primary-500 hover:text-primary-600 transition-colors flex items-center gap-1"
            >
              <i className="ri-arrow-left-line" /> 返回分类浏览
            </Link>
          </div>
        </div>
      </main>
    </>
  );
}